export interface QuestionAnswer {
  question: string;
  answer: string;
}

export interface HelpFaqCategory {
  key: string;
  labelName: string;
  questionCount: number;
  questionAnswerList: QuestionAnswer[];
}

export const HELP_FAQ_LIST: HelpFaqCategory[] = [
  {
    key: 'gettingStarted',
    labelName: 'Getting Started',
    questionCount: 5,
    questionAnswerList: [
      {
        question: 'What is AST used for?',
        answer:
          'AST is the Audit Support Tool used to manage the full lifecycle of R&C reviews - planning, conduct, reporting, issue management, and action plans - in one platform, replacing AuditBoard, SharePoint, Excel, and email-based tracking.',
      },
      {
        question: 'How do I log in to AST?',
        answer:
          "AST uses your Accenture Enterprise ID. If you're unable to access the system, contact your R&C Supervisor or Admin to confirm your access has been provisioned.",
      },
      {
        question: 'What do I see when I first log in?',
        answer:
          "You'll land on your Dashboard, showing a summary of information relevant to your role - reviews you're assigned to, pending approvals, or requests, depending on whether you're a Reviewer, Supervisor, Manager, or Admin.",
      },
      {
        question: 'What review types does AST support?',
        answer:
          'AST supports four review types: Standard, Go-Live, On-Demand, and Special Project reviews.',
      },
      {
        question: "Who can I contact if I have an issue not covered here?",
        answer:
          'Contact your R&C Supervisor or Manager for support with your specific review or task.',
      },
    ],
  },
  {
    key: 'reviewManagement',
    labelName: 'Review Management',
    questionCount: 10,
    questionAnswerList: [
      {
        question: "What's the difference between the four review types?",
        answer:
          'Standard reviews follow the standard planning-to-reporting cycle. Go-Live reviews assess readiness before and after a system go-live, anchored on a Go-Live Date. On-Demand reviews are ad-hoc reviews triggered outside the regular cycle. Special Project reviews cover specific project types (e.g. Foxtrot, Eclipse, Jupiter, BCM).',
      },
      {
        question: "Can I edit a review after it's Scheduled?",
        answer:
          "Once the Schedule section is submitted, both Schedule and Review Basic Details become permanently read-only. You'll continue through the Review Cycle - Plan, Conduct, and Report - from that point.",
      },
      {
        question: 'How is the Review Name generated?',
        answer:
          'The Review Name is auto-generated using the formula FY[Year]-[ShortCode]-[ClientName]-[ProjectName]-[PrimaryLocation] and cannot be edited manually.',
      },
      {
        question: 'How are activity target dates calculated?',
        answer:
          'All target dates are calculated automatically using working business days from your Fieldwork Start/End dates (or Go-Live Date for Go-Live reviews). Weekends are excluded, and any date falling on a weekend moves to the following Monday.',
      },
      {
        question: 'Can I add a new location to a review after creation?',
        answer:
          'Yes, but only while the review is in Planned status and before the Schedule section is submitted. Once Schedule is submitted, no new locations can be added.',
      },
      {
        question: 'What happens if I need to cancel a review?',
        answer:
          'You can raise a Review Cancellation request from the Review Details page. Once approved by your Manager, the cancellation is permanent and the review becomes read-only across all sections.',
      },
      {
        question: 'Do I have to complete Conduct Review activities in a strict order?',
        answer:
          "Only mandatory activities are sequence-enforced - you can't jump ahead to a mandatory activity before completing the one before it. Optional activities have no such restriction: you can mark them complete before, after, or independent of mandatory activities, in any order.",
      },
      {
        question: "Do optional activities affect the Review's overall progress status?",
        answer:
          'No. Conduct Review Stage Progress is driven only by mandatory activity completion. Completing optional activities has no effect on stage progress or review status, regardless of when they are marked done.',
      },
      {
        question: 'If I edit or update something already completed, does it change the review status?',
        answer:
          'No. For example, adding or removing a document on an already-completed activity does not retrigger or change the review status - the status only changes when a new mandatory activity milestone is reached, not when existing data on a completed step is edited.',
      },
      {
        question: 'Can I download templates to help me during the Review Cycle?',
        answer:
          'Yes. A "Download the Latest Template" link is available in the Plan Review section while the review is in Scheduled status. It opens a popup showing all available templates for that stage of the review - each downloadable individually, or all at once as a ZIP file via Download All. If no templates have been seeded yet, the popup will tell you to contact your Administrator.',
      },
    ],
  },
  {
    key: 'issueManagement',
    labelName: 'Issue Management',
    questionCount: 8,
    questionAnswerList: [
      {
        question: 'What are the possible statuses for an Issue?',
        answer:
          'Issues move through defined statuses in their lifecycle - from identification through to resolution via an Action Plan. Refer to the Issue Status reference for the full list and sequence.',
      },
      {
        question: "Can I edit an Issue after it's been approved?",
        answer:
          "Once an Issue is approved and moves out of Draft, most fields become read-only. The exact editable fields depend on the Issue's current status.",
      },
      {
        question: 'What happens if an Issue is set to Inactive?',
        answer:
          'An Inactive Issue opens in Read Only mode when viewed or edited. Its linked Action Plans remain visible at the bottom of the page, also in Read Only mode.',
      },
      {
        question: "Who approves an Issue before it's finalized?",
        answer:
          'Your assigned R&C Supervisor (Primary or Secondary) reviews and approves or rejects Issues submitted for approval.',
      },
      {
        question: 'Can I upload documents against an Issue?',
        answer:
          'Yes, but files must not be classified as Confidential, Highly Confidential, Personal Data, or Restricted Data. Supported file types include PDF, Word, Excel, PowerPoint, images, and ZIP, up to 20MB per file.',
      },
      {
        question: 'How do I link an Action Plan to an Issue?',
        answer:
          'When creating or editing an Issue, you can either link an existing Action Plan by searching and selecting it, or create a new Action Plan directly from the Issue form.',
      },
      {
        question: 'What does "Blue" rating mean for an Issue, and how does it affect the Action Plan?',
        answer:
          'Blue rating means the Issue is not currently active in the monitoring cycle - either closed, out of scope, or intentionally excluded from tracking. When an Issue is rated Blue, its linked Action Plan automatically gets the status "Closed - Blue Not Tracked" and becomes read-only - you cannot manually set an Action Plan to this status unless the Issue rating is Blue. Only Red, Yellow, Green, or Not Rated Issues can have Action Plans with Open, Open-Ageing & Static, or Closed status.',
      },
      {
        question: 'If my Issue is rated Blue, does Supervisor approval still matter?',
        answer:
          'Yes. Supervisor approval only affects the Issue status - it has no impact on Action Plan status. When a Blue-rated Issue is approved, its status moves to Closed directly, and the linked Action Plan stays as Closed - Blue Not Tracked.',
      },
    ],
  },
  {
    key: 'actionPlans',
    labelName: 'Action Plans',
    questionCount: 6,
    questionAnswerList: [
      {
        question: 'What are the possible statuses for an Action Plan?',
        answer:
          'An Action Plan can be: Open (report published, owner needs to work on remediation), Open-Ageing & Static (leadership has accepted it remains open while risk is not fully mitigated), Closed (evidence produced to close it), Closed - Blue Not Tracked (automatically set when the linked Issue is rated Blue), or Archived - Ageing and Static (typically 365+ days old, awaiting target closure).',
      },
      {
        question: 'Can I manually set an Action Plan to "Closed - Blue Not Tracked"?',
        answer:
          "No. This status is automatically applied only when the linked Issue's rating is Blue - you cannot select it manually from the dropdown for Red, Yellow, or Green rated Issues.",
      },
      {
        question: 'Can I add multiple Action Plans while creating an Issue?',
        answer:
          'Yes. Use the "Add Another Action Plan" button in Step 2 of the Issue creation flow. Each Action Plan section can be expanded or collapsed independently, and a Remove button is available if there is more than one.',
      },
      {
        question: 'What happens if I save an Action Plan as Draft without selecting a status?',
        answer:
          "The Action Plan status will show as Blank - Empty. This isn't an official status - it just means no selection was made. If you try to submit the linked Issue for approval while an Action Plan is Blank - Empty, you'll get a validation error.",
      },
      {
        question: 'Can I link an existing Action Plan to a new Issue instead of creating a new one?',
        answer:
          'Yes. In Step 2 of the Issue flow, use "Add Existing Action Plan" to search and link one. Note that Action Plans with status Closed - Blue Not Tracked won\'t appear in this list unless the current Issue is also rated Blue.',
      },
      {
        question: "Does approving an Issue change its Action Plan's status?",
        answer:
          'No. Supervisor approval only affects Issue status - it never changes Action Plan status directly. However, if all Action Plans linked to an Issue are already Closed at the time of approval, the Issue status will move to Closed.',
      },
    ],
  },
  {
    key: 'bulkUpload',
    labelName: 'Bulk Upload',
    questionCount: 7,
    questionAnswerList: [
      {
        question: 'What can I bulk upload in AST?',
        answer:
          'Two things: Standard Reviews (via the Bulk Upload Standard Review template), and Issues with their Action Plans (via the Bulk Upload Issue & Action Plan template) - for any review type.',
      },
      {
        question: 'What status do bulk-uploaded reviews land in?',
        answer:
          'All bulk-uploaded reviews land in Draft status. You must open each one individually in the UI and submit it manually to activate it - bulk upload does not auto-submit reviews.',
      },
      {
        question: 'Can I bulk upload Go-Live, On-Demand, or Special Project reviews?',
        answer:
          'No. Bulk upload for review creation is only available for Standard Reviews. Go-Live, On-Demand, and Special Project reviews must be created individually through the UI.',
      },
      {
        question: 'Can I bulk upload Issues for any review type?',
        answer:
          'Yes. The Issue & Action Plan bulk upload template supports all four review types - Standard, Go-Live, On-Demand, and Special Project.',
      },
      {
        question: 'What status do bulk-uploaded Issues land in?',
        answer:
          'All bulk-uploaded Issues land in Draft status, same as bulk-uploaded reviews. They must be reviewed and submitted individually afterward.',
      },
      {
        question: 'How does a bulk-uploaded Issue get linked to the correct review?',
        answer:
          'Each Issue row in the template must reference a valid Review ID - this is how the system links the uploaded Issue to its correct review.',
      },
      {
        question: 'Can I bulk upload parent/child review relationships?',
        answer:
          'No. The Parent/Child field has been removed from the Bulk Upload Standard Review template and is deferred to a future release, post MVP.',
      },
    ],
  },
  {
    key: 'requestManagement',
    labelName: 'Request Management',
    questionCount: 8,
    questionAnswerList: [
      {
        question: 'What types of requests can I raise in AST?',
        answer:
          'Add New User, Accenture Operations Service Line, Review Cancellation, Review Plan Not Applicable, and Revised Target Publication Date. More request types may be added over time.',
      },
      {
        question: 'Where do I go to raise or view requests?',
        answer:
          'Click the Request tab in the top navigation to see the Request Listing page. Review Cancellation and Revised Publication Date requests are the exception - these are raised directly from within the Review Details page, not from the Request tab.',
      },
      {
        question: 'What statuses can a request have?',
        answer: 'New (submitted, waiting for action), Approved, or Rejected.',
      },
      {
        question: 'Who can approve or reject requests?',
        answer:
          'Depends on your role and request type. Reviewers, R&C Members, and Supervisors can only View requests. Managers can Approve/Reject Review Cancellation, Review Plan Not Applicable, and Revised Publication Date requests. Admin can Approve/Reject/Delete any request type.',
      },
      {
        question: 'Can I raise more than one of the same request for the same review?',
        answer:
          'No. If a request already exists for that Review ID, the system blocks a duplicate submission and shows a warning.',
      },
      {
        question: 'What happens if my request is rejected?',
        answer:
          "You'll see the status change to Rejected in the Request Listing grid, along with the mandatory Reason for Rejection the approver provided.",
      },
      {
        question: 'What happens to the review once my "Plan Review Not Applicable" request is approved?',
        answer:
          'The Plan Phase section of that review becomes completely read-only - no tasks or fields under Plan Phase can be completed or updated afterward.',
      },
      {
        question: 'Can a Manager approve their own request?',
        answer:
          'No. If a Primary Manager raises a request, they cannot approve it themselves - it goes to the Secondary Manager (if assigned) or Admin instead.',
      },
    ],
  },
  {
    key: 'timelineCalculator',
    labelName: 'Timeline Calculator',
    questionCount: 6,
    questionAnswerList: [
      {
        question: 'Does using the Calculator create a review or save any data?',
        answer:
          "No. The Calculator is a standalone planning tool - no review is created and no data is saved anywhere in the system. It's purely for offline planning.",
      },
      {
        question: 'Who can access the Calculator?',
        answer: 'Any logged-in user, regardless of role.',
      },
      {
        question: 'How do I choose which formula applies to my calculation?',
        answer:
          'Select your review type and location (Single or Multiple) from the dropdown - there are 8 combinations across Standard, Go-Live, On-Demand, and Special Project reviews. The correct input fields and formulas apply automatically based on your selection.',
      },
      {
        question: 'Why does Go-Live only ask for 2 dates while other review types also ask for 2?',
        answer:
          'All review types use 2 anchor dates. For Standard, On-Demand, and Special Project reviews, you enter Fieldwork Start and End dates. For Go-Live reviews, you enter Go-Live Date and Post Go-Live Testing End Date - all other activity dates are calculated automatically from these two anchors.',
      },
      {
        question: 'Can I export my calculated results?',
        answer:
          'Yes. Click Export to Excel to download a file with your input values and all calculated activity dates, named using the pattern AST_Timeline_Calculator_[ReviewType]_[Date].xlsx.',
      },
      {
        question: 'What happens if a calculated date falls on a weekend?',
        answer: 'It automatically moves to the following Monday. All calculations use working business days only.',
      },
    ],
  },
  {
    key: 'rolesAndAccess',
    labelName: 'Roles & Access',
    questionCount: 8,
    questionAnswerList: [
      {
        question: 'What role does every user get by default?',
        answer:
          'Every user is automatically assigned R&C Member as their default system-level role. This controls access to non-review modules like Dashboard, Reports, Help, and Request - regardless of what review-level roles they are assigned elsewhere.',
      },
      {
        question: 'What are the review-level roles, and how are they different from R&C Member?',
        answer:
          "Reviewer, Supervisor, and Manager are assigned per review, not system-wide. The same person can be R&C Manager on one review and have no role at all on another - each review's role assignment is independent.",
      },
      {
        question: 'Can I see reviews I am not assigned to?',
        answer:
          "Yes. All users can view all reviews in the system. If you're not assigned a role on a specific review, you can only view it as a read-only R&C Member - you can't create, edit, or submit anything on it.",
      },
      {
        question: 'Can the same person be both Primary and Secondary for the same role on a review?',
        answer:
          'No. For example, if someone is assigned as Primary R&C Manager, they cannot also be Secondary R&C Manager on that same review. They can, however, be assigned to a different role like Supervisor or Reviewer.',
      },
      {
        question: 'Who can change team assignments (Reviewer, Supervisor, Manager) on a review?',
        answer:
          'Manager can change Primary and Secondary Supervisor and Reviewer assignments. Supervisor can only change Primary and Secondary Reviewer assignments. Reviewer and R&C Member cannot change any role assignment.',
      },
      {
        question: 'Until when can team assignments be edited?',
        answer:
          'Team assignment fields remain editable in any status after Scheduled, as long as not all Issues and Action Plans on that review are Closed. Once the review status becomes Closed, all role assignment fields become permanently read-only.',
      },
      {
        question: 'If I created an Issue, can I approve it myself?',
        answer:
          'No. Approval is always routed away from the creator. If a Supervisor creates the Issue, it routes to the Secondary Supervisor (or Primary Manager if no Secondary is assigned). If a Manager creates it, it routes to the Primary Supervisor.',
      },
      {
        question: 'Can R&C Member ever get edit access just by being added to a review team?',
        answer:
          'No. R&C Member permissions stay read-only even if added to a review team - access only changes to editable when they are explicitly assigned a review-level role like Reviewer, Supervisor, or Manager.',
      },
    ],
  },
];
